import { resetPlanStatus } from "../../redux/slices/activePlanSlice";
import { resetState } from "../../redux/slices/deleteUserSlice";
import {
  clearFinchatData,
  finchatMessageCancelAll,
} from "../../redux/slices/finchatSlice";
import { resetValidUserState } from "../../redux/slices/validateUserSlice";
import { clearUser, requestSignOut } from "./authActions";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { AnyAction } from "redux-saga";
import { rmAllCookiesAndStorage } from "../../redux/sagas/authSaga";
import { urlIsAppScheme } from "../Session/helpers";
import { ResetPasswordErrorCodes, ResetPasswordErrorCodesTypes } from "./constants";
import { formatSeconds } from "../../helpers";
import { FormData, ValidationResult } from "../../assets/interfaces/interfaces";
import { isValidPhoneNumber } from "libphonenumber-js";
import { TextField } from "@mui/material";
import PhoneNumberInput from "./PhoneNumberInput";

export const doSignOutClear = (
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>,
) => {
  dispatch(clearFinchatData());
  rmAllCookiesAndStorage();
  dispatch(resetState());
  dispatch(resetValidUserState());
  dispatch(requestSignOut());
  dispatch(clearUser());
  dispatch(resetPlanStatus());
  // Lets force to cancel a message if there is any loading
  dispatch(finchatMessageCancelAll());
  // Lets get to the sign in flow if its not mobile
  const redirect = window.location.search.split("redirect_uri=")[1] ?? "/";
  const isMobileApp = urlIsAppScheme(redirect);
  if (isMobileApp) {
    return;
  }
  window.location.href = "/";
};

export const phraseErrorMessage = (errorCode: ResetPasswordErrorCodesTypes, secondsLeft?: number) => {
  if (errorCode === ResetPasswordErrorCodes.PASSWORD_RESET_ATTEMPT_LIMIT_REACHED && secondsLeft) {
    return `An email has already been sent to you from the TradeAlgo team. Please wait ${formatSeconds(secondsLeft)} before requesting another one.`;
  }
  return "An error has occurred. Please try again.";
};

export const validateSignInForm = (email: string, password: string): ValidationResult => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  const errors = { email: "", password: "" };
  let isValid = true;

  if (!email) {
    errors.email = "Email is required.";
    isValid = false;
  }
  else if (!emailRegex.test(email)) {
    errors.email = "Please enter a valid email address.";
    isValid = false;
  }

  if (!password) {
    errors.password = "Password is required.";
    isValid = false;
  }

  return { valid: isValid, errors };
};

export const validateForm = ({ email, name, phoneNumber, password }: FormData): ValidationResult => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
  const errors: { email?: string; name?: string; phoneNumber?: string; password?: string } = {};
  let isValid = true;
  if (!email) {
    errors.email = "Email is required.";
    isValid = false;
  }
  else if (!emailRegex.test(email)) {
    errors.email = "Please enter a valid email address.";
    isValid = false;
  }

  if (!name) {
    errors.name = "Name is required.";
    isValid = false;
  }

  if (!phoneNumber) {
    errors.phoneNumber = "Phone number is required.";
    isValid = false;
  }
  else if (!isValidPhoneNumber(phoneNumber)) {
    errors.phoneNumber = "Please enter a valid phone number.";
    isValid = false;
  }

  if (password.length < 6) {
    errors.password = "Password must be at least 6 characters long.";
    isValid = false;
  }

  return { valid: isValid, errors };
};

export const renderTextField = (label: string, value: string, onChange, error: string, type?: string) => (
  <TextField
    label={label}
    variant="outlined"
    value={value}
    onChange={onChange}
    className="w-[300px] md:w-[340px] outlined-input labelText"
    error={!!error}
    helperText={error}
    type={type}
  />
);
export const renderPhoneField = (label: string, value: string, onChange, error: string) => (
  <PhoneNumberInput className="w-[300px] md:w-[340px] outlined-input labelText" label={label} error={!!error} helperText={error} value={value} onChange={onChange} />
);
